// @ts-nocheck - TODO: properly type this file!

import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Col, Divider, Flex, Form } from 'antd';

import {
  AddItemButton,
  CustomButton,
  CustomRadioGroup,
  CustomTitle,
} from '../../../components';
import imgAvatar from '@/assets/images/ImgAvatar.svg';
import DirectContactForm from './DirectContactForm';
import SupportContactPicker from './SupportContactPicker';
import SupportContactForm from './SupportContactForm';
import type { AddNewContactFormProps, Contact, ContactFormData } from './types';
import ChangelogTooltip from './ChangelogTooltip';
import styles from './DirectContactManage.module.css';
type ContactType = 'direct_contact' | 'support';
type PreferredChannel = 'phone' | 'email' | 'sms' | 'none';

function AddNewContactFormInner(
  {
    setWorkingContacts,
    setIsAddingSupport,
    isAddingSupport,
    drawerOpen,
    setEditingContact,
    isEditing,
    title,
    contactList,
    form,
    providerData,
    handleEditFromList,
    handleOpenAddNote,
    editContact,
    onCancel,
    onSaved,
    contactType,
    setContactType,
    workingContacts,
    onUpsertDraft,
    onSupportChannelSwitch,
  }: AddNewContactFormProps,
  ref
) {
  const [pendingOps, setPendingOps] = useState<
    Record<string, { action: 'add' | 'remove'; contact: Contact }>
  >({});
  const [supportChannel, setSupportChannel] = useState<'phone' | 'email'>(
    'phone'
  );
  const [preferredChannel, setPreferredChannel] =
    useState<PreferredChannel | null>(null);

  interface DirectorySupportApiContact {
    id: string | number;
    contact_type: 'general_support';
    contact_source: 'directory';
    email: string;
    phone_number: string;
    country_code: string;
    country_region: string;
    operating_hours: string;
    usage_notes: string;
    label: string;
    courier_name?: string;
    topic?: string;
    formatted_phone?: string;
    contact_info?: string;
    channel?: string;
    [key: string]: unknown;
  }

  const normalizeAsDirectorySupport = (c: Contact): Contact => {
    const api: DirectorySupportApiContact = {
      ...(c.api ?? {}),
      id: c.id,
      contact_type: 'general_support',
      contact_source: 'directory',
      email: c.api?.email ?? c.email ?? '',
      phone_number: c.api?.phone_number ?? '',
      country_code: c.api?.country_code ?? '',
      country_region: c.api?.country_region ?? c.country ?? '',
      operating_hours: c.api?.operating_hours ?? c.hours ?? '',
      usage_notes: c.api?.usage_notes ?? c.note ?? '',
      label: c.api?.label ?? c.name ?? '',
    };

    return {
      ...c,
      contact_type: 'general_support',
      api,
      isActive: true,
    };
  };

  const isDirect = contactType === 'direct_contact';
  const displayTitle =
    !isDirect && isAddingSupport
      ? editContact
        ? 'Update general support contact'
        : 'Add general support contact'
      : title;
  const primaryButtonText = editContact
    ? 'Save edit'
    : isDirect
      ? 'Add contact'
      : isAddingSupport
        ? 'Add support contact'
        : 'Save';
  useImperativeHandle(
    ref,
    () => ({
      isSupportFormActive: isAddingSupport,
      backFromSupport: () => {
        setIsAddingSupport(false);
        setContactType('support');
      },
    }),
    [isAddingSupport, setContactType, setIsAddingSupport]
  );
  useEffect(() => {
    if (contactType === 'direct_contact' && isAddingSupport) {
      setIsAddingSupport(false);
    }
  }, [contactType, isAddingSupport, setIsAddingSupport]);

  const handleCancel = () => {
    form.resetFields();
    setEditingContact(null);
    setIsAddingSupport(false);
    onCancel?.();
  };

  useEffect(() => {
    if (!editContact) return;
    setPreferredChannel(editContact.preferred_channel ?? null);

    const nextValues: Partial<ContactFormData> = {
      ...editContact,
      id: editContact.id ?? null,
      preferred_channel: editContact.preferred_channel ?? undefined,
      phone_number: editContact.phone_number ?? editContact.phone ?? '',
      email: editContact.email ?? '',
      country_code:
        editContact.country_code ??
        (form.getFieldValue('country_code') as string) ??
        '',
      country_region:
        editContact.country_region ??
        (form.getFieldValue('country_region') as string) ??
        '',
      is_primary_contact:
        editContact.is_primary_contact ??
        (editContact.contact_type === 'direct' ? false : undefined) ??
        (editContact.id
          ? editContact.is_primary_contact
          : contactList?.length === 0),
      note:
        editContact.note ?? editContact.notes ?? editContact.usage_notes ?? '',
      hours_24_7:
        editContact.hours_24_7 === true ||
        editContact.hours_24_7 === 'true' ||
        editContact.operating_hours === '24/7',
      operating_hours: editContact.operating_hours,
    };

    form.setFieldsValue(nextValues);
  }, [editContact, contactList?.length, form, drawerOpen]);

  useEffect(() => {
    form.setFieldsValue({
      preferred_channel: preferredChannel ?? undefined,
    } as Partial<ContactFormData>);
  }, [preferredChannel, form]);

  const withPrimaryFlags = (c: Contact, isPrimary: boolean): Contact => ({
    ...c,
    is_primary: isPrimary,
    is_primary_contact: isPrimary,
    api: c.api ? { ...c.api, is_primary_contact: isPrimary } : c.api,
  });

  const withOrderIndex = (c: Contact, order_index: number): Contact => ({
    ...c,
    order_index,
    api: c.api ? { ...c.api, order_index } : c.api,
  });

  const enforceSinglePrimaryAndReorder = (
    list: Contact[],
    primaryId: string | number
  ): Contact[] => {
    const pid = String(primaryId);
    const idx = list.findIndex((c) => String(c.id) === pid);
    if (idx === -1) return list;

    const primary = withPrimaryFlags(list[idx], true);
    const others = list
      .filter((_, i) => i !== idx)
      .map((c) => withPrimaryFlags(c, false));

    const reordered = [primary, ...others];
    return reordered.map((c, i) => withOrderIndex(c, i));
  };

  const handlePrimaryClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (contactType === 'support' && !isAddingSupport) {
      const ops = Object.values(pendingOps);
      setWorkingContacts((prev) => {
        const list = Array.isArray(prev) ? [...prev] : [];
        const indexById = new Map<string, number>();
        list.forEach((c, i) => indexById.set(String(c.id), i));

        for (const { action, contact } of ops) {
          const id = String(contact.id);
          const idx = indexById.get(id);

          if (action === 'add') {
            if (idx == null) {
              const normalized = normalizeAsDirectorySupport(contact);
              list.push(normalized);
              indexById.set(id, list.length - 1);
            } else {
              list[idx] = { ...list[idx], isActive: true };
            }
          } else if (action === 'remove') {
            if (idx != null) {
              list.splice(idx, 1);
              indexById.delete(id);
              indexById.clear();
              list.forEach((c, i) => indexById.set(String(c.id), i));
            }
          }
        }

        return list;
      });
      setPendingOps({});
      onSaved?.();
      return;
    }

    const submit = (kind: 'direct' | 'general_support') =>
      form
        .validateFields()
        .then(() => {
          const all = form.getFieldsValue(true);
          const nowId = all?.id || `tmp-${Date.now()}`;
          const base = { id: nowId };
          const order_index = all?.order_index ?? workingContacts?.length ?? 0;

          const selectedChannel =
            (all?.preferred_channel as 'phone' | 'email' | undefined) ??
            preferredChannel ??
            'phone';

          const cleaned = {
            email: selectedChannel === 'email' ? (all.email ?? '') : '',
            phone_number:
              selectedChannel === 'phone' ? (all.phone_number ?? '') : '',
            country_code:
              selectedChannel === 'phone' ? (all.country_code ?? '') : '',
            country_region:
              selectedChannel === 'phone' ? (all.country_region ?? '') : '',
          };

          if (kind === 'direct') {
            const apiDraft = {
              ...base,
              contact_type: 'direct',
              contact_source: 'user_defined',
              name: all.name ?? '',
              title: all.title ?? '',
              email: all.email ?? '',
              phone_number: all.phone_number ?? '',
              country_code: all.country_code ?? '',
              country_region: all.country_region ?? '',
              preferred_channel: all.preferred_channel ?? 'none',
              preferred_language: all.preferred_language ?? 'English',
              is_primary_contact: !!all.is_primary_contact,
              notes: all.notes ?? all.note ?? '',
              order_index,
            };

            setWorkingContacts((prev) => {
              const list = Array.isArray(prev) ? [...prev] : [];
              const idx = list.findIndex((c) => String(c.id) === String(nowId));

              const nextItem: Contact = {
                ...(idx >= 0 ? list[idx] : ({} as Contact)),
                ...apiDraft,
                id: nowId,
                kind: 'direct',
                isActive: true,
                is_primary: !!apiDraft.is_primary_contact,
                is_primary_contact: !!apiDraft.is_primary_contact,
                api: { ...(idx >= 0 ? list[idx].api : {}), ...apiDraft },
              };

              if (idx >= 0) list[idx] = nextItem;
              else list.push(nextItem);
              return apiDraft.is_primary_contact
                ? enforceSinglePrimaryAndReorder(list, nowId)
                : list;
            });

            onUpsertDraft?.(apiDraft);
            onSaved?.();
            return;
          }

          const apiDraft = {
            ...base,
            contact_type: 'general_support',
            contact_source: 'user_defined',
            label: all.label ?? '',
            topic: all.topic ?? 'General',
            email: cleaned.email,
            phone_number: cleaned.phone_number,
            country_code: cleaned.country_code,
            country_region: cleaned.country_region,
            preferred_channel: selectedChannel,
            operating_hours: all.operating_hours ?? '',
            timezone: all.timezone ?? '',
            typical_hold_time: all.typical_hold_time ?? '',
            notes: all.notes ?? all.note ?? '',
            order_index,
          };
          onUpsertDraft?.(apiDraft);
          onSaved?.();
        })
        .catch(() => {});

    if (contactType === 'direct_contact') return submit('direct');
    if (isAddingSupport) return submit('general_support');
  };
  return (
    <div className={styles.addContactRoot}>
      <CustomTitle
        style={{ position: 'relative', left: '0px' }}
        text={displayTitle}
        fontSize='20px'
        lineHeight='28px'
        fontWeight='700'
        marginBottom='0px'
      />
      {!isAddingSupport && (
        <div>
          <CustomRadioGroup
            value={contactType}
            horizontal={false}
            onChange={(e) => {
              setContactType(e.target.value as ContactType);
              setIsAddingSupport(false);
            }}
            options={[
              {
                label: 'Direct Contact',
                value: 'direct_contact',
                description:
                  'i.e., your customer support contact, account manager, etc',
              },
              {
                label: 'General support contact',
                value: 'support',
                description: '',
              },
            ].filter((opt) => {
              if (title === 'Update contact') {
                return opt.value !== 'support';
              }
              return true;
            })}
          />
          <Divider
            style={{ borderColor: 'var(--gray-300)', margin: '12px 0' }}
          />
        </div>
      )}

      <div className={styles.addContactBody}>
        {isDirect ? (
          <>
            <Flex
              align='center'
              justify='space-between'
              gap={12}
              style={{ marginBottom: 16 }}
            >
              <Flex align='center' gap={12}>
                <img
                  src={imgAvatar}
                  alt=''
                  className={styles.addContactAvatar}
                />
                <CustomTitle
                  text='Add direct contact'
                  fontSize='16px'
                  lineHeight='20px'
                  fontWeight='600'
                  marginBottom='0px'
                />
              </Flex>
              {isEditing && <ChangelogTooltip providerData={providerData} />}
            </Flex>

            <Form form={form} layout='vertical' requiredMark={false}>
              <DirectContactForm
                form={form}
                contactList={contactList as Contact[]}
                preferredChannel={preferredChannel}
                setPreferredChannel={(channel) =>
                  setPreferredChannel(channel as PreferredChannel)
                }
              />
            </Form>
          </>
        ) : isAddingSupport ? (
          <Form form={form} layout='vertical' requiredMark={false}>
            <SupportContactForm
              form={form}
              preferredChannel={preferredChannel}
              setPreferredChannel={(next) => {
                if (next === 'phone' || next === 'email') {
                  if (supportChannel !== next) {
                    const id = form.getFieldValue('id');
                    if (id != null) {
                      onSupportChannelSwitch?.(supportChannel, id);
                    }
                    setSupportChannel(next);
                  }
                }
                setPreferredChannel(next);
              }}
            />
          </Form>
        ) : (
          <>
            <SupportContactPicker
              handleEditFromList={handleEditFromList}
              handleOpenAddNote={handleOpenAddNote}
              providerData={providerData}
              workingContacts={workingContacts}
              onChange={(delta) => {
                setPendingOps((prev) => ({
                  ...prev,
                  [delta.id]: { action: delta.action, contact: delta.contact },
                }));
              }}
            />
            <Col span={24}>
              <Divider
                style={{ borderColor: 'var(--gray-300)', marginTop: '14px' }}
              />
            </Col>
            <Col span={24}>
              <AddItemButton
                gap='24px'
                width='28px'
                height='28px'
                marginTop='14px'
                text='Add general support contact'
                fontSize='12px'
                fontWeight='600'
                onClick={() => {
                  setEditingContact(null);
                  setContactType('support');
                  form?.resetFields();
                  setPreferredChannel(null);
                  setIsAddingSupport(true);
                }}
              />
            </Col>
          </>
        )}
      </div>

      <div className={styles.addContactFooter}>
        <CustomButton
          text={primaryButtonText}
          bgColor='black'
          color='white'
          height='48px'
          width='100%'
          fontSize='16px'
          fontWeight='600'
          boxShadow='none'
          borderRadius='8px'
          onClick={handlePrimaryClick}
        />

        <CustomButton
          onClick={handleCancel}
          text='Cancel'
          bgColor='transparent'
          color='black'
          fontSize='16px'
          fontWeight='400'
          boxShadow='none'
          height='auto'
          marginTop='20px'
          marginBottom='10px'
          width='100%'
          borderRadius='8px'
        />
      </div>
    </div>
  );
}

const AddNewContactForm = forwardRef(AddNewContactFormInner);
AddNewContactForm.displayName = 'AddNewContactForm';

export default AddNewContactForm;
